/**
 * SanPlumb — useSessionState
 *
 * Hook con la misma signatura de useState pero persiste el valor en
 * `sessionStorage` (§II/§XV: sessionStorage únicamente). Read on mount,
 * write on cada cambio.
 *
 * Las primitivas de lectura/escritura JSON son funciones puras en
 * `engine/sessionState.ts` (testeadas con un Storage mock).
 *
 * Uso:
 *   const [counts, setCounts] = useSessionState(SESSION_KEYS.HUNTER_COUNTS,
 *                                                initialCounts());
 */

import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { readJson, writeJson } from '../engine/sessionState';

/** Devuelve `window.sessionStorage` o null en entornos sin DOM (SSR/test). */
function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function useSessionState<T>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  // Lazy init: lee del storage en el primer render.
  const [value, setValue] = useState<T>(() => readJson(getStorage(), key, initial));

  // Evita escribir en el primer render (lo recién leído es lo mismo).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    writeJson(getStorage(), key, value);
  }, [key, value]);

  return [value, setValue];
}
